import {
    HStack,
    VStack,
    Box,
    Button,
    IconButton,
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo } from 'react';
import { ColumnDefinitionEnum } from '@mark43/cad-api';
import { QueueStore } from '../queues/store/queue-store';
import { useQueueStore } from '../queues/context/use-queue-store';
import { QueueFilterMultiselect } from '../common/components/queues-filter-select';
import { useEventQueueFilterOptions } from '../event-queues/get-event-filter-options';
import {
    SetBoloQueueFiltersArguments,
    useSetFrBoloQueueFilterState,
} from './state/fr-shared-queue-filters-context';
import { useFrEventsListAgencyTypeOptions } from './use-fr-events-list-agency-type-options';
import { FrEventsListAgencyTypeOption } from './types';
import { Option } from '../forms/option';

export const FrFilterMenu = observer(function FrFilterMenu(): React.ReactElement {
    const queueStore = useQueueStore() as QueueStore;
    const agencyTypeOptions = useFrEventsListAgencyTypeOptions();
    const effectiveQueueClasses = queueStore.workspacePanel.queue.queueClasses.filter((cls) =>
        agencyTypeOptions.some((opt) => opt.value === cls)
    );
    const {
        additionalCfsOptions,
        dispatchAreaOptions,
        eventPrimarySubdivisionOptions,
        primarySubdivisionName,
        alarmLevelOptions,
        cfsOptions,
        agencyOptions,
        eventLabelOptions,
        priorityOptions,
    } = useEventQueueFilterOptions();
    const { setQueueListFilters } = useSetFrBoloQueueFilterState();

    const eventQueueFilters = useMemo(
        () => queueStore.workspacePanel.queue.eventQueueFilters ?? [],
        [queueStore.workspacePanel.queue.eventQueueFilters]
    );

    const allSelectedEventQueueFilterValues = useMemo(() => {
        return eventQueueFilters
            .filter(
                (eventQueueFilter) =>
                    eventQueueFilter.columnDefinition !== ColumnDefinitionEnum.ADDITIONAL_CFS.name
            )
            .map((eventQueueFilter) => eventQueueFilter.entityId);
    }, [eventQueueFilters]);

    const allSelectedAdditionalCfsFilterValues = useMemo(() => {
        return eventQueueFilters
            .filter(
                (eventQueueFilter) =>
                    eventQueueFilter.columnDefinition === ColumnDefinitionEnum.ADDITIONAL_CFS.name
            )
            .map((eventQueueFilter) => eventQueueFilter.entityId);
    }, [eventQueueFilters]);

    const allOptions = useMemo(() => {
        return [
            ...dispatchAreaOptions,
            ...eventPrimarySubdivisionOptions,
            ...alarmLevelOptions,
            ...cfsOptions,
            ...agencyOptions,
            ...eventLabelOptions,
            ...priorityOptions,
        ];
    }, [
        dispatchAreaOptions,
        eventPrimarySubdivisionOptions,
        alarmLevelOptions,
        cfsOptions,
        agencyOptions,
        eventLabelOptions,
        priorityOptions,
    ]);

    const getBoloOptions = useCallback(
        (entityIds: number[]): SetBoloQueueFiltersArguments => {
            if (!entityIds.length) {
                return {};
            }
            const selectedOptions = allOptions.filter((option) => entityIds.includes(option.value));
            return {
                dispatchAreaOptions: selectedOptions.filter(
                    (o) => o.columnDefinition === 'DISPATCH_AREA'
                ),
                subdivisionOptions: selectedOptions.filter((o) => o.columnDefinition === 'BEAT'),
            };
        },
        [allOptions]
    );

    const onChangeEventQueueFilters = useCallback(
        (entityIds: number[]) => {
            const selectedOptions = allOptions
                .filter((option) => entityIds.includes(option.value))
                .map((option) => ({
                    value: option.value,
                    label: option.label,
                    columnDefinition: option.columnDefinition,
                }));

            queueStore.setEventQueueFilters(selectedOptions);
            setQueueListFilters(getBoloOptions(entityIds));
        },
        [allOptions, queueStore, setQueueListFilters, getBoloOptions]
    );

    const onChangeQueueClasses = useCallback(
        (newDisplayedClasses: FrEventsListAgencyTypeOption[]) => {
            queueStore.setQueueClasses(newDisplayedClasses);
        },
        [queueStore]
    );

    const onChangeAdditionalCfsFilters = useCallback(
        (entityIds: number[]) => {
            const selectedOptions = additionalCfsOptions
                .filter((option) => entityIds.includes(option.value))
                .map((option) => ({
                    value: option.value,
                    label: option.label,
                    columnDefinition: ColumnDefinitionEnum.ADDITIONAL_CFS.name,
                }));

            queueStore.upsertEventQueueFilters(selectedOptions, 'ADDITIONAL_CFS');
        },
        [queueStore, additionalCfsOptions]
    );

    const clearAll = useCallback(() => {
        onChangeEventQueueFilters([]);
        onChangeAdditionalCfsFilters([]);
        setQueueListFilters({ subdivisionOptions: [], dispatchAreaOptions: [] });
    }, [onChangeEventQueueFilters, setQueueListFilters, onChangeAdditionalCfsFilters]);

    const isActive =
        !!queueStore.workspacePanel.queue.eventQueueFilters.length ||
        !!effectiveQueueClasses.length;

    const showClearAll =
        allSelectedAdditionalCfsFilterValues.length > 0 ||
        allSelectedEventQueueFilterValues.length > 0;

    const filterComponentsMap = useMemo(() => {
        return [
            {
                label: ColumnDefinitionEnum.DISPATCH_AREA.displayName,
                attributeOptions: dispatchAreaOptions,
                values: allSelectedEventQueueFilterValues,
                onChange: onChangeEventQueueFilters,
                dataTestId: 'dispatch-area-filter',
            },
            {
                label: primarySubdivisionName ?? ColumnDefinitionEnum.BEAT.displayName,
                attributeOptions: eventPrimarySubdivisionOptions,
                values: allSelectedEventQueueFilterValues,
                onChange: onChangeEventQueueFilters,
                dataTestId: 'primary-subdivision-filter',
            },
            {
                label: ColumnDefinitionEnum.CLASS.displayName,
                attributeOptions: agencyTypeOptions,
                values: effectiveQueueClasses,
                onChange: onChangeQueueClasses,
                dataTestId: 'event-class-filter',
            },
            {
                label: ColumnDefinitionEnum.ADDITIONAL_CFS.displayName,
                attributeOptions: additionalCfsOptions,
                values: allSelectedAdditionalCfsFilterValues,
                onChange: onChangeAdditionalCfsFilters,
                dataTestId: 'additional-cfs-filter',
            },
            {
                label: ColumnDefinitionEnum.ALARM_LEVEL.displayName,
                attributeOptions: alarmLevelOptions,
                values: allSelectedEventQueueFilterValues,
                onChange: onChangeEventQueueFilters,
                dataTestId: 'alarm-level-filter',
            },
            {
                label: ColumnDefinitionEnum.CFS_NUMBER_AND_TEXT.displayName,
                attributeOptions: cfsOptions,
                values: allSelectedEventQueueFilterValues,
                onChange: onChangeEventQueueFilters,
                dataTestId: 'cfs-filter',
            },
            {
                label: ColumnDefinitionEnum.AGENCY.displayName,
                attributeOptions: agencyOptions,
                values: allSelectedEventQueueFilterValues,
                onChange: onChangeEventQueueFilters,
                dataTestId: 'event-agency-filter',
            },
            {
                label: ColumnDefinitionEnum.EVENT_LABELS.displayName,
                attributeOptions: eventLabelOptions,
                values: allSelectedEventQueueFilterValues,
                onChange: onChangeEventQueueFilters,
                dataTestId: 'event-labels-filter',
            },
            {
                label: ColumnDefinitionEnum.PRIORITY.displayName,
                attributeOptions: priorityOptions,
                values: allSelectedEventQueueFilterValues,
                onChange: onChangeEventQueueFilters,
                dataTestId: 'priority-filter',
            },
        ].sort((a, b) => a.label.localeCompare(b.label));
    }, [
        additionalCfsOptions,
        alarmLevelOptions,
        cfsOptions,
        agencyOptions,
        eventLabelOptions,
        priorityOptions,
        onChangeAdditionalCfsFilters,
        allSelectedAdditionalCfsFilterValues,
        dispatchAreaOptions,
        allSelectedEventQueueFilterValues,
        onChangeEventQueueFilters,
        primarySubdivisionName,
        eventPrimarySubdivisionOptions,
        agencyTypeOptions,
        onChangeQueueClasses,
        effectiveQueueClasses,
    ]);

    const onOpenChangeHandler = (open: boolean) => {
        if (!open) {
            queueStore.saveFrWorkspaceWindow();
        }
    };

    return (
        <Popover hasArrow={false} onOpenChange={onOpenChangeHandler}>
            <PopoverTrigger asChild>
                <IconButton
                    aria-label="filter by event queue filters"
                    icon="Filter"
                    isActive={isActive}
                    variant="outline"
                    data-testid="FR_FILTER_MENU_BUTTON"
                />
            </PopoverTrigger>
            <PopoverContent align="start" style={{ paddingRight: 0 }}>
                <VStack pr={0} pb={0}>
                    <HStack justifyContent="flex-end" alignItems="center" w="100%" pr="22px">
                        {showClearAll && (
                            <Button variant="ghost" onClick={clearAll}>
                                Clear All
                            </Button>
                        )}
                    </HStack>
                    <Box
                        pr="12px"
                        minWidth="350px"
                        maxWidth="350px"
                        maxHeight="500px"
                        overflowY="auto"
                        textTransform="none"
                        className="custom-scrollbar"
                    >
                        {filterComponentsMap.map((filter) => (
                            <QueueFilterMultiselect
                                key={filter.label}
                                label={filter.label}
                                dataTestId={filter.dataTestId}
                                attributeOptions={
                                    filter.attributeOptions as Option<
                                        number | FrEventsListAgencyTypeOption
                                    >[]
                                }
                                values={filter.values}
                                onChange={(values) =>
                                    filter.onChange(
                                        values as number[] & FrEventsListAgencyTypeOption[]
                                    )
                                }
                            />
                        ))}
                    </Box>
                </VStack>
            </PopoverContent>
        </Popover>
    );
});
